/* React and hooks */
import React, { useState, useEffect, useCallback } from 'react';

/* API client */
import apiClient from '../../apiClient';

/* Common UI components */
import Button from '../../Component/common/Button/Button';
import Header from '../../Component/common/Header/Header';

/* Invoice-specific components */
import InvoiceCreate from './InvoiceCreate';
import InvoiceModal from './InvoiceModal';
import InvoiceListTable from '../../Component/invoice/invoiceListTable';

import { supplier as defaultSupplier } from '../../constants/invoiceConstants';

/* Styles */
import './invoice.css';

const Invoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('All'); // 'PI', 'TI', or 'All'
  const [defaultInvoiceType, setDefaultInvoiceType] = useState('PROFORMA INVOICE');

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/invoices');
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, []);

  const updateInvoice = useCallback(async (id, updateData) => {
    try {
      await apiClient.put(`/api/invoices/${id}`, updateData);
      alert('Invoice updated successfully');
      // Optionally refresh invoice list or update state
    } catch (error) {
      console.error('Failed to update invoice:', error);
      alert('Failed to update invoice');
    }
  }, []);

  const deleteInvoice = useCallback(async (id) => {
    try {
      await apiClient.delete(`/api/invoices/${id}`);
      setInvoices((prevInvoices) => prevInvoices.filter((inv) => inv.id !== id));
      alert('Invoice deleted successfully');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      alert('Failed to delete invoice');
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleRowClick = useCallback((invoice) => {
    setSelectedInvoice(invoice);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedInvoice(null);
  }, []);

  const fetchInvoiceById = useCallback(async (id) => {
    try {
      const response = await apiClient.get(`/api/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      alert('Failed to fetch invoice details');
      return null;
    }
  }, []);

  const onEdit = useCallback(async (invoice) => {
    const detailedInvoice = await fetchInvoiceById(invoice.id);
    if (detailedInvoice) {
      // Transform detailedInvoice to match InvoiceCreate expected prop structure
      const transformedInvoice = {
        id: detailedInvoice.id,
        invoice_no: detailedInvoice.invoice_no,
        invoice_type: detailedInvoice.invoice_type,
        invoice_date: detailedInvoice.invoice_date,
        placeOfSupply: detailedInvoice.supply_place || '',
        name: detailedInvoice.name,
        terms_conditions: detailedInvoice.terms_conditions || '',
        recipient: {
          name: detailedInvoice.name,
          address: detailedInvoice.recipient_address || detailedInvoice.address || '',
          state: detailedInvoice.recipient_state || detailedInvoice.state || '',
          gstin: detailedInvoice.GST_no,
          email: detailedInvoice.email,
          contact: detailedInvoice.phone_no
        },
        products: (() => {
          try {
            return JSON.parse(detailedInvoice.invoice_data || '[]');
          } catch (e) {
            return [];
          }
        })().map(product => ({
          packageType: product.packageType || '',
          duration: product.duration || '',
          description: product.description || '',
          hsn: product.hsn || '',
          qty: product.qty || 1,
          rate: product.rate || 0,
          discount: product.discount || 0,
          cgst: product.cgst || 0,
          sgst: product.sgst || 0,
          igst: product.igst || 0
        })),
        // placeOfSupply: detailedInvoice.placeOfSupply || '',
         supplier: {
          ...defaultSupplier,
          name: detailedInvoice.supplier_name || defaultSupplier.name,
          state: detailedInvoice.supplier_state  || defaultSupplier.state,
          gstin: detailedInvoice.GST_no || defaultSupplier.gstin,
          contact: detailedInvoice.supplier_contact || defaultSupplier.contact,
          email: detailedInvoice.supplier_email || defaultSupplier.email,
          address: detailedInvoice.supplier_address || defaultSupplier.address,
          pan: detailedInvoice.pan || defaultSupplier.pan
        },
        bank: detailedInvoice.bank || '',
        // Add other fields as needed
      };
      setSelectedInvoice(transformedInvoice);
      setShowCreate(true);
    }
  }, [fetchInvoiceById]);

  const onCopy = useCallback((invoice) => {
    alert(`Copy invoice ${invoice.invoice_no}`);
  }, []);

  const onDelete = useCallback((invoice) => {
    deleteInvoice(invoice.id);
  }, [deleteInvoice]);

  const onDownload = useCallback(async (invoice) => {
    try {
      const response = await apiClient.get(`/api/invoices/${invoice.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_no}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Failed to download invoice');
    }
  }, []);

    // Filter invoices based on active tab
  const getFilteredInvoices = () => {
    if (activeTab === 'PI') {
      return invoices.filter(inv => (inv.invoice_type || '').toLowerCase() === 'proforma invoice');
    } else if (activeTab === 'TI') {
      return invoices.filter(inv => (inv.invoice_type || '').toLowerCase() === 'tax invoice');
    }
    return invoices;
  };

  return (
    <div className="invoice-container">
      {/* <h2>Invoice</h2> */}
       <div className="header-row">
        <Header title="TAX / PROFORMA INVOIE" />

        {!showCreate && (
            <Button
            onClick={() => {
                setSelectedInvoice({ invoice_type: defaultInvoiceType });
                setShowCreate(true);
            }}
            className="create-btn"
            >
            Create TI / PI
            </Button>
        )}
        </div>

      {!showCreate && (
        <>
          {/* <Button onClick={() => setShowCreate(true)} className="btn create-invoice-btn"> */}
          {/* Tab Navigation */}
          <div className="invoice-tab-container">
            <button
              className={`invoice-tab ${activeTab === 'All' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('All');
                setDefaultInvoiceType('PROFORMA INVOICE');
              }}
            >
              All
            </button>

            <button
              className={`invoice-tab ${activeTab === 'PI' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('PI');
                setDefaultInvoiceType('PROFORMA INVOICE');
              }}
            >
              Proforma Invoice (PI)
            </button>
            <button
              className={`invoice-tab ${activeTab === 'TI' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('TI');
                setDefaultInvoiceType('TAX INVOICE');
              }}
            >
              Tax Invoice (TI)
            </button>
          </div>

          {/* <Button onClick={() => {
            setSelectedInvoice({ invoice_type: defaultInvoiceType });
            setShowCreate(true);
          }} className="btn create-invoice-btn">

            Create Invoice
          </Button> */}

          <InvoiceListTable
            // invoices={invoices}
            invoices={getFilteredInvoices()}
            onEdit={onEdit}
            onCopy={onCopy}
            onDelete={onDelete}
            onDownload={onDownload}
            onRowClick={handleRowClick}
          />
          {selectedInvoice && <InvoiceModal invoice={selectedInvoice} onClose={closeModal} />}
        </>
      )}

      {showCreate && (
        <InvoiceCreate
            invoice={selectedInvoice}
            isEditable={true}
            onBack={() => {
            setShowCreate(false);
            setSelectedInvoice(null);
            }}
        />
        )}

      {/* {showCreate && (
        <>
          <Button onClick={() => {
            setShowCreate(false);
            setSelectedInvoice(null);
          }}>
            Back to Invoice List
          </Button>
          <InvoiceCreate invoice={selectedInvoice} isEditable={true} />
        </>
      )} */}
    </div>
  );
};

export default Invoice;
